import type { RevenueDisplayMode } from "@productlane/db"

import { escapeHtml } from "./escape-html"
import { formatRevenue } from "./format-revenue"

export type MacroVariableGroup = "Contact" | "Company" | "Teammate"

export interface MacroVariableContact {
	name?: string | null
	email?: string | null
}

export interface MacroVariableCompany {
	name?: string | null
	domains?: readonly string[] | null
	domain?: string | null
	size?: number | null
	revenue?: number | null
	tierName?: string | null
	statusName?: string | null
	linearOwnerName?: string | null
}

export interface MacroVariableContext {
	contact?: MacroVariableContact | null
	company?: MacroVariableCompany | null
	user?: { name?: string | null } | null
	revenueFormatting?: RevenueDisplayMode | null
	currency?: string | null
}

export interface MacroVariable {
	key: string
	label: string
	group: MacroVariableGroup
	description: string
	aliases: string[]
	// Used when the thread carries no value, so a macro never sends a raw token.
	fallback: string
	resolve: (ctx: MacroVariableContext) => string | null | undefined
}

export const macroVariableToken = (key: string) => `[${key}]`

const clean = (value?: string | null) => value?.trim() || null

const nameParts = (contact?: MacroVariableContact | null): string[] => {
	const full = clean(contact?.name)
	if (!full) return []
	return full.split(/\s+/)
}

const companyDomain = (company?: MacroVariableCompany | null) =>
	clean(company?.domains?.[0]) ?? clean(company?.domain)

export const MACRO_VARIABLES: readonly MacroVariable[] = [
	{
		key: "name",
		label: "First name",
		group: "Contact",
		description: "The customer's first name",
		aliases: ["first", "firstname", "contact", "customer"],
		fallback: "there",
		resolve: (ctx) => nameParts(ctx.contact)[0],
	},
	{
		key: "surname",
		label: "Last name",
		group: "Contact",
		description: "The customer's last name",
		aliases: ["last", "lastname", "family"],
		fallback: "",
		resolve: (ctx) => nameParts(ctx.contact).slice(1).join(" "),
	},
	{
		key: "full_name",
		label: "Full name",
		group: "Contact",
		description: "The customer's full name",
		aliases: ["fullname", "contact"],
		fallback: "there",
		resolve: (ctx) => clean(ctx.contact?.name),
	},
	{
		key: "email",
		label: "Email",
		group: "Contact",
		description: "The customer's email address",
		aliases: ["mail", "address"],
		fallback: "",
		resolve: (ctx) => clean(ctx.contact?.email),
	},
	{
		key: "company",
		label: "Company name",
		group: "Company",
		description: "The company the customer belongs to",
		aliases: ["account", "organisation", "organization"],
		fallback: "your company",
		resolve: (ctx) => clean(ctx.company?.name),
	},
	{
		key: "company_domain",
		label: "Company domain",
		group: "Company",
		description: "The company's primary domain",
		aliases: ["website", "url", "domain"],
		fallback: "",
		resolve: (ctx) => companyDomain(ctx.company),
	},
	{
		key: "company_tier",
		label: "Company tier",
		group: "Company",
		description: "The tier set on the company",
		aliases: ["plan", "tier"],
		fallback: "",
		resolve: (ctx) => clean(ctx.company?.tierName),
	},
	{
		key: "company_status",
		label: "Company status",
		group: "Company",
		description: "The status set on the company",
		aliases: ["status", "stage"],
		fallback: "",
		resolve: (ctx) => clean(ctx.company?.statusName),
	},
	{
		key: "company_size",
		label: "Company size",
		group: "Company",
		description: "Headcount stored on the company",
		aliases: ["headcount", "employees", "seats"],
		fallback: "",
		resolve: (ctx) =>
			typeof ctx.company?.size === "number" ? String(ctx.company.size) : null,
	},
	{
		key: "company_revenue",
		label: "Company revenue",
		group: "Company",
		description: "Revenue stored on the company",
		aliases: ["arr", "mrr", "money"],
		fallback: "",
		resolve: (ctx) => {
			if (typeof ctx.company?.revenue !== "number") return null
			return formatRevenue({
				revenue: ctx.company.revenue,
				revenueFormatting: ctx.revenueFormatting ?? "ANNUAL",
				currency: ctx.currency ?? undefined,
			})
		},
	},
	{
		key: "company_owner",
		label: "Company owner",
		group: "Company",
		description: "The Linear owner of the company",
		aliases: ["owner", "account manager"],
		fallback: "",
		resolve: (ctx) => clean(ctx.company?.linearOwnerName),
	},
	{
		key: "agent_name",
		label: "Your name",
		group: "Teammate",
		description: "The name of the teammate sending the reply",
		aliases: ["me", "sender", "signature", "teammate"],
		fallback: "",
		resolve: (ctx) => clean(ctx.user?.name),
	},
]

export const MACRO_VARIABLE_BY_KEY: Record<string, MacroVariable> =
	Object.fromEntries(
		MACRO_VARIABLES.map((variable) => [variable.key, variable]),
	)

// The leading whitespace is captured so an empty value does not leave a gap.
const TOKEN_PATTERN = /([ \t]*)\[([a-z][a-z0-9_]*)\]/gi

export function searchMacroVariables(
	query: string,
	limit = MACRO_VARIABLES.length,
): MacroVariable[] {
	const needle = query.trim().toLocaleLowerCase()
	if (!needle) return MACRO_VARIABLES.slice(0, limit)

	return MACRO_VARIABLES.filter((variable) => {
		if (variable.key.includes(needle)) return true
		if (variable.label.toLocaleLowerCase().includes(needle)) return true
		return variable.aliases.some((alias) => alias.includes(needle))
	}).slice(0, limit)
}

export function resolveMacroVariable(
	variable: MacroVariable,
	ctx: MacroVariableContext,
): string {
	return clean(variable.resolve(ctx)) ?? variable.fallback
}

// Unknown tokens stay as typed: a macro may hold real square brackets.
export function resolveMacroVariables(
	html: string,
	ctx: MacroVariableContext,
): string {
	return html.replace(TOKEN_PATTERN, (match, spacing: string, key: string) => {
		const variable = MACRO_VARIABLE_BY_KEY[key.toLocaleLowerCase()]
		if (!variable) return match
		const value = resolveMacroVariable(variable, ctx)
		return value ? `${spacing}${escapeHtml(value)}` : ""
	})
}

export function hasMacroVariables(html: string): boolean {
	return MACRO_VARIABLES.some((variable) =>
		html.toLocaleLowerCase().includes(macroVariableToken(variable.key)),
	)
}
